import React from "react";
import debounce from "lodash/debounce";
import moment from "moment";
import styled from "styled-components";
import { queryCache } from "react-query";
import { useRouter } from "next/router";
import { Button, Alert, Row, Col, DatePicker, Select, Empty, Spin } from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import useSearchUser from "hooks/api/user/useSearchUser";
import { NotificationStateContext } from "context/UI/Notification/NotificationState";
import { apiAxios } from "services/HttpServices";
import { joiResolver } from "@hookform/resolvers";
import Joi from "@hapi/joi";

import { TextInputSkeleton } from "lib/loader/shared/textInputSkeleton";
import { TInput } from "components/form/Field/TInput";
import { clean } from "lib/utils";

const { Option } = Select;

const tasks = Joi.object()
  .keys({
    title: Joi.string().required().min(3),
    desc: Joi.string().allow(null),
  })
  .unknown();

const todoSchema = Joi.object({
  tasks: Joi.array().items(tasks),
});

const formatTasks = (tasks) => {
  const newTasks = tasks.map((task) => {
    const formattedTasks = {
      ...task,
      due_date: task.due_after
        ? moment().add(task.due_after, "days").utc().format()
        : null,
      asignees: undefined,
    };
    return formattedTasks;
  });

  return newTasks;
};

const formatData = (resData) => {
  const newData = resData?.map((d) => ({
    key: d.id,
    value: d.id,
    text: d.name,
  }));

  return newData;
};

const ManageTodoListForm = (props) => {
  const [error, setError] = React.useState("");
  const [keyword, setKeyword] = React.useState("");
  const router = useRouter();
  const { id } = router.query;
  const { data, status: searchUserStatus } = useSearchUser(keyword);
  const {
    state: notificationState,
    setSuccessMessage,
    clearSuccessMessage,
    setLoading,
    finishLoading,
  } = React.useContext(NotificationStateContext);
  const { handleSubmit, errors, control, reset, setValue } = useForm({
    defaultValues: {
      tasks: props.tasks
        ? formatTasks(props.tasks)
        : [
            {
              title: undefined,
              desc: undefined,
              due_date: undefined,
              asignees: undefined,
            },
          ],
    },
    resolver: joiResolver(todoSchema),
  });
  const { fields, append, remove } = useFieldArray({
    control,
    name: "tasks",
  });

  const searchUserByKeyword = (keyword) => {
    setKeyword(keyword);
  };

  const onSubmit = async (data) => {
    setError("");
    setLoading("createTodo");
    const formattedTasks = data?.tasks?.map((task) => {
      const newFormattedTask = {
        ...task,
        due_date: task.due_date ? task.due_date.format() : null,
      };
      if (task.asignees && task.asignees.length > 0) {
        newFormattedTask["asignees"] = task.asignees.map((asignee) => ({
          id: parseInt(asignee.value),
        }));
      } else {
        newFormattedTask["asignees"] = null;
      }
      clean(newFormattedTask);
      return newFormattedTask;
    });

    try {
      const res = await apiAxios.post(`/todos/${id}`, {
        tasks: formattedTasks,
      });

      queryCache.setQueryData(["job-detail", id.toString()], function (
        oldData
      ) {
        const newData = {
          ...oldData,
          todo: res.data.data,
        };
        return newData;
      });
      props.setDoneCreate(true);
      finishLoading("createTodo");
      setSuccessMessage("Succesfully created Todo");
    } catch (e) {
      finishLoading("createTodo");
      setError(e.response.data.message);
    }
  };

  React.useEffect(() => {
    reset({
      tasks: props.tasks
        ? formatTasks(props.tasks)
        : [
            {
              title: undefined,
              desc: undefined,
              due_date: undefined,
              asignees: undefined,
            },
          ],
    });
  }, [props.tasks]);

  return (
    <>
      {error && (
        <div className="alert">
          <Alert message={error} type="error" showIcon />
        </div>
      )}
      <>
        {props.status === "loading" ? (
          <ItemContainer>
            <Row gutter={4} align="middle">
              <Col xs={6}>
                <TextInputSkeleton />
              </Col>
              <Col xs={8}>
                <TextInputSkeleton />
              </Col>
              <Col xs={4}>
                <TextInputSkeleton />
              </Col>
              <Col xs={5}>
                <TextInputSkeleton />
              </Col>

              <Col xs={1} align="middle">
                <TextInputSkeleton />
              </Col>
            </Row>
            <Row gutter={4} align="middle">
              <Col xs={6}>
                <TextInputSkeleton />
              </Col>
              <Col xs={8}>
                <TextInputSkeleton />
              </Col>
              <Col xs={4}>
                <TextInputSkeleton />
              </Col>
              <Col xs={5}>
                <TextInputSkeleton />
              </Col>

              <Col xs={1} align="middle">
                <TextInputSkeleton />
              </Col>
            </Row>
            <Row gutter={4} align="middle">
              <Col xs={6}>
                <TextInputSkeleton />
              </Col>
              <Col xs={8}>
                <TextInputSkeleton />
              </Col>
              <Col xs={4}>
                <TextInputSkeleton />
              </Col>
              <Col xs={5}>
                <TextInputSkeleton />
              </Col>

              <Col xs={1} align="middle">
                <TextInputSkeleton />
              </Col>
            </Row>
            <Row gutter={4} align="middle">
              <Col xs={6}>
                <TextInputSkeleton />
              </Col>
              <Col xs={8}>
                <TextInputSkeleton />
              </Col>
              <Col xs={4}>
                <TextInputSkeleton />
              </Col>
              <Col xs={5}>
                <TextInputSkeleton />
              </Col>

              <Col xs={1} align="middle">
                <TextInputSkeleton />
              </Col>
            </Row>
          </ItemContainer>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)}>
            {fields.length > 0 && (
              <>
                {fields.map((item, index) => {
                  return (
                    <ItemContainer key={item.id}>
                      <Row gutter={4} align="middle">
                        <Col xs={6}>
                          <TInput
                            errors={errors}
                            name={`tasks[${index}].title`}
                            placeholder="Task title"
                            defaultValue={item.title}
                            propsInput={{
                              control: control,
                            }}
                          />
                        </Col>
                        <Col xs={8}>
                          <TInput
                            errors={errors}
                            name={`tasks[${index}].desc`}
                            placeholder="Task description..."
                            defaultValue={item.desc}
                            propsInput={{
                              control: control,
                            }}
                          />
                        </Col>
                        <Col xs={4}>
                          <Controller
                            control={control}
                            name={`tasks[${index}].due_date`}
                            defaultValue={
                              item.due_date ? moment(item.due_date) : null
                            }
                            render={() => (
                              <DatePicker
                                onChange={(e) => {
                                  setValue(`tasks[${index}].due_date`, e);
                                }}
                                defaultValue={
                                  item.due_date ? moment(item.due_date) : null
                                }
                                style={{
                                  width: "100%",
                                  marginBottom: "0.75rem",
                                }}
                                format="DD/MM/YYYY"
                              />
                            )}
                          />
                        </Col>
                        <Col xs={5}>
                          <Controller
                            name={`tasks[${index}].asignees`}
                            control={control}
                            defaultValue={
                              item.asignees ? item.asignees : undefined
                            }
                            render={({ value }) => {
                              return (
                                <Select
                                  mode="multiple"
                                  placeholder="Assign users"
                                  labelInValue
                                  filterOption={false}
                                  value={value}
                                  onSearch={debounce(searchUserByKeyword, 800)}
                                  style={{
                                    width: "100%",
                                    marginBottom: "0.75rem",
                                  }}
                                  notFoundContent={
                                    searchUserStatus === "loading" ? (
                                      <Spin size="small" />
                                    ) : (
                                      <Empty
                                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                                      />
                                    )
                                  }
                                  onChange={(e) => {
                                    setKeyword("");
                                    setValue(`tasks[${index}].asignees`, e, {
                                      shouldDirty: true,
                                    });
                                  }}
                                >
                                  {formatData(data)?.map((d) => (
                                    <Option key={d.value}>{d.text}</Option>
                                  ))}
                                </Select>
                              );
                            }}
                          />
                        </Col>

                        <Col xs={1} align="middle">
                          <Button
                            type="primary"
                            danger
                            icon={<DeleteOutlined />}
                            size="medium"
                            onClick={() => remove(index)}
                            style={{ marginBottom: "0.75rem" }}
                          />
                        </Col>
                      </Row>
                    </ItemContainer>
                  );
                })}
              </>
            )}
            <ButtonContainer>
              <Button
                type="outlined"
                icon={<PlusOutlined />}
                onClick={() => {
                  append({
                    title: null,
                    desc: null,
                    due_date: null,
                    asignees: [],
                  });
                }}
              >
                Add Task
              </Button>

              <Button
                htmlType="submit"
                type="primary"
                loading={notificationState.loading.createTodo}
              >
                Create todo
              </Button>
            </ButtonContainer>
          </form>
        )}
      </>
    </>
  );
};

const ItemContainer = styled.div`
  margin-bottom: ${(props) => props.theme.spacing[4]};
  border-bottom: 1px solid ${(props) => props.theme.colors.grey[200]};
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

export default ManageTodoListForm;
